import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    Modal,
    TextField,
    Tooltip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { fbmApi } from 'api/fbm';
import { inboundTruckApi as api } from 'api/inboundTrucks';
import { logisticsApi } from 'api/logistics';
import { packagingApi } from 'api/packaging';
import { procurementApi } from 'api/procurement';
import { warehouseApi } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications, SearchBar } from 'shared';
import dayjs from 'utils/dayjsConfig';

import { combineGroupedPartialPoItems, expandPartialPoItemMapIds } from './partialPoItemUtils';

// The table to show items
function ViewItemsTable({ parentRow, selectedPO, setSelectedPO, setSelectedRow, setViewSkusModalOpen }) {
    const [truckID, setTruckID] = useState(parentRow.original.id);
    const [rows, setRows] = useState([]);
    const [packagingRows, setPackagingRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [addItemModalOpen, setAddItemModalOpen] = useState(false);
    const [pos, setPos] = useState([]);
    const [packagingPos, setPackagingPos] = useState([]);
    const [partnerASNs, setPartnerASNs] = useState([]);
    const [partnerPackagingASNs, setPartnerPackagingASNs] = useState([]);
    const [multiTruckPO, setMultiTruckPO] = useState(false);
    const [editPickupDateModalOpen, setEditPickupDateModalOpen] = useState(false);

    const user = useContext(UserContext);

    const packagingColumns = [
        { accessor: 'id', Header: 'ID' },
        { accessor: 'scheduledDeliveryDate', Header: 'Scheduled Delivery' },
        { accessor: 'status', Header: 'PO Status' },
        { accessor: 'vendorName', Header: 'Vendor' },
        { accessor: 'numPallets', Header: 'Num Pallets' },
        { accessor: 'totalWeight', Header: 'Weight' },
        { accessor: 'referenceNumber', Header: 'Reference' },
        { accessor: 'estimatedFreightCost', Header: 'Estimated Freight Cost' },
        {
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ row }) => (
                <Button
                    id={`Remove_${row.id}`}
                    onClick={async () => {
                        const response = await api.removePackagingItemFromInboundTruck(
                            parentRow.original.id,
                            row.original.id,
                        );

                        if (response.status === false) {
                            setNotification({ text: `Can't remove item ${response.msg}`, level: 'error' });
                            return;
                        }

                        getItemsInTable();
                    }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    const openPickupDateModal = (row) => {
        setEditPickupDateModalOpen(true);
        setSelectedPO(row.id);
    };

    const columns = [
        { accessor: 'id', Header: 'Purchase Order ID' },
        { accessor: 'priority', Header: 'Priority', type: 'checkbox' },
        { accessor: 'scheduledDeliveryDate', Header: 'Scheduled Delivery' },
        { accessor: 'shipperName', Header: 'Shipper' },
        { accessor: 'numSkus', Header: 'Num SKUs', hide: !multiTruckPO },
        { accessor: 'vendorName', Header: 'Vendor' },
        { accessor: 'buyers', Header: 'Buyers' },
        { accessor: 'numPallets', Header: 'Num Pallets' },
        { accessor: 'totalWeight', Header: 'Weight' },
        { accessor: 'pickupDate', Header: 'Pickup Date' },
        {
            accessor: 'actualPickupDate',
            Header: 'Actual Pick Up Date',
            editable: (row) => row.original.actualPickupDate === '',
            customEdit: (row) => {
                openPickupDateModal(row);
            },
        },
        { accessor: 'pickupDateCount', Header: 'Pickup Date Update Count' },
        { accessor: 'status', Header: 'PO Status' },
        { accessor: 'deliveryType', Header: 'Delivery Type' },
        { accessor: 'pickupNumber', Header: 'Pickup Number' },
        { accessor: 'weekNumber', Header: 'Week Number' },
        { accessor: 'estimatedFreightCost', Header: 'Estimated Freight Cost', hide: multiTruckPO },
        { accessor: 'poShippingStatus', Header: 'PO Shipping Status' },
        {
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <Button
                    id={`Remove_${row.id}`}
                    onClick={async () => {
                        const response = await api.removeItemFromInboundTruck(parentRow.original.id, row.original.id);

                        if (response.status === false) {
                            setNotification({ text: `Can't remove item ${response.msg}`, level: 'error' });
                            return;
                        }

                        const truckResponse = await api.receiveTruck({ truckID: parentRow.original.id });

                        if (truckResponse.status === false) {
                            setNotification({
                                text: `Failed to receive truck ${truckResponse.msg}`,
                                level: 'error',
                            });
                            return;
                        }

                        getItemsInTable();
                    }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    if (parentRow.original.poType === 'partial') {
        columns.push({
            accessor: 'viewSkus',
            Header: 'View SKUs',
            Cell: ({ row }) => (
                <Button
                    id={`ViewSkus_${row.id}`}
                    onClick={() => {
                        setSelectedPO(row.original.id);
                        setSelectedRow(parentRow);
                        setViewSkusModalOpen(true);
                    }}
                >
                    View
                </Button>
            ),
        });
    }

    let getItemsInTable = async () => {
        let poType;
        if (parentRow.original.poType === 'partial') {
            poType = 'partial';
        }
        const response = await api.getTruckDetails(truckID, user.getFacilityID(), poType);

        if (response.status === false) {
            setNotification({ text: `No purchase orders found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        setRows(
            response.data.output.pos.map((row) => {
                row.scheduledDeliveryDate = dayjs(row.scheduledDeliveryDate.date).format('MM/DD/YYYY');
                row.pickupDate = row.pickupDate ? dayjs(row.pickupDate.date).format('MM/DD/YYYY') : '';
                row.actualPickupDate = row.actualPickupDate
                    ? dayjs(row.actualPickupDate.date).format('MM/DD/YYYY')
                    : '';
                if (row.buyers && Array.isArray(row.buyers)) {
                    row.buyers = row.buyers.map((item) => item.name).join(', ');
                }
                return row;
            }),
        );
        setPackagingRows(
            response.data.output.packagingPos.map((row) => {
                row.scheduledDeliveryDate = dayjs(row.scheduledDeliveryDate.date).format('MM/DD/YYYY');
                return row;
            }),
        );
    };

    const getPos = async () => {
        const pos = await procurementApi.getTruckAssignablePurchaseOrdersByFacility(user.getFacilityID());
        if (!pos.status) {
            setNotification({ level: 'error', text: 'Failed to get POs' });
        }
        const filteredRows = pos.data.rows.filter((item) => {
            if (!item.truckID || item.truckID === 'N/A') {
                return true;
            }
            return false;
        });
        setPos(filteredRows);
    };

    const getParnerASNs = async () => {
        if (parentRow.original.fbmPartner == '') {
            return;
        }
        const asns = await fbmApi.getASNs(parentRow.original.fbmPartner);
        if (!asns.status) {
            setNotification({ level: 'error', text: 'Failed to get FBM ASNs' });
            return;
        }
        const filteredRows = asns.data.rows.filter((item) => {
            if (item.facilityID != user.getFacilityID()) {
                return false;
            }
            if (item.status != 'PLACED') {
                return false;
            }
            return true;
        });
        setPartnerASNs(filteredRows);
    };

    const getPartnerPackagingASNs = async () => {
        if (parentRow.original.fbmPartner == '') {
            return;
        }

        const packASNs = await fbmApi.getPackagingASNs(parentRow.original.fbmPartner);
        if (!packASNs.status) {
            setNotification({ level: 'error', text: 'Failed to get FBM packaging ASNs' });
            return;
        }
        const filteredRows = packASNs.data.rows.filter((item) => {
            if (item.facilityID != user.getFacilityID()) {
                return false;
            }
            if (item.status != 'PLACED') {
                return false;
            }
            return true;
        });
        setPartnerPackagingASNs(filteredRows);
    };

    const getPackagingPos = async () => {
        const ppos = await packagingApi.getPurchaseOrders('PLACED', user.getFacilityID());
        if (!ppos.status) {
            setNotification({ level: 'error', text: 'Failed to get packaging POs' });
            return;
        }
        const filteredRows = ppos.data.rows.filter((item) => {
            if (!item.truckID) {
                return true;
            }
            return false;
        });
        setPackagingPos(filteredRows);
    };

    useEffect(() => {
        getItemsInTable();
        getPos();
        getPackagingPos();
        getParnerASNs();
        getPartnerPackagingASNs();

        if (parentRow.original.poType === 'partial') {
            setMultiTruckPO(true);
        }
    }, []);

    return (
        <div className="batch-table">
            <Notifications options={notification} />
            {addItemModalOpen && (
                <AddItemModal
                    poType={parentRow.original.poType}
                    deliveryType={parentRow.original.deliveryType}
                    truckID={truckID}
                    pos={pos}
                    packagingPos={packagingPos}
                    partnerASNs={partnerASNs}
                    partnerPackagingASNs={partnerPackagingASNs}
                    isFbmPartnerTruck={parentRow.original.fbmPartner}
                    closeModal={() => {
                        setAddItemModalOpen(false);
                        getItemsInTable();
                    }}
                    setNotification={setNotification}
                    currentPosOnTruck={rows}
                />
            )}
            {editPickupDateModalOpen && (
                <EditActualPickupDateModal
                    closeModal={() => {
                        setEditPickupDateModalOpen(false);
                        getItemsInTable();
                    }}
                    setNotification={setNotification}
                    selectedPO={selectedPO}
                />
            )}
            {rows.length > 0 && <DataTable title="Grocery Purchase Orders" columns={columns} data={rows} editable />}
            {packagingRows.length > 0 && (
                <DataTable title="Packaging Purchase Orders" data={packagingRows} columns={packagingColumns} />
            )}
            <Button
                id="AddPO"
                onClick={() => {
                    setAddItemModalOpen(true);
                }}
            >
                {parentRow.original.fbmPartner ? 'Add ASN' : 'Add PO'}
            </Button>
        </div>
    );
}

function EditActualPickupDateModal({ closeModal, selectedPO, setNotification }) {
    const formFields = [
        {
            name: 'actualPickupDate',
            inputElement: 'date',
            disableTextInput: false,
            label: 'Actual Pickup Date',
            gridValue: 12,
            inputProps: { required: true },
        },
    ];

    return (
        <Dialog open onClose={closeModal}>
            <DialogTitle>
                PLEASE NOTE: The Actual Pickup Date cannot be edited after submitting. Please be sure it is correct.
            </DialogTitle>
            <DialogContent>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const item = {};
                        item.actualPickupDate = formData.actualPickupDate;

                        const res = await procurementApi.updatePurchaseOrder(selectedPO, item);
                        if (res.status === false) {
                            setNotification({ level: 'error', text: `Error modifying purchase order ${res.msg}` });
                        } else {
                            closeModal();
                        }
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

// The modal to add items to an inbound truck
function AddItemModal({
    closeModal,
    currentPosOnTruck,
    deliveryType,
    isFbmPartnerTruck,
    packagingPos,
    partnerASNs,
    partnerPackagingASNs,
    pos,
    poType,
    setNotification,
    truckID,
}) {
    const user = useContext(UserContext);
    const [error, setError] = useState(null);
    const [partialPoItems, setPartialPoItems] = useState([]);
    const [selectedPartialPoSkus, setSelectedPartialPoSkus] = useState([]);
    const [filteredPos, setFilteredPos] = useState([]);

    let dropDownOptions = ['Purchase Order'];

    useEffect(() => {
        if (poType !== 'partial') {
            dropDownOptions = dropDownOptions.push('Packaging Purchase Order');
        }

        // filter out POs already on this truck
        if (currentPosOnTruck) {
            const posToRemove = currentPosOnTruck.map((item) => item.id);
            setFilteredPos(pos.filter((item) => !posToRemove.includes(item.id)));
        }
    }, []);

    const groceryPOsOrASNs = isFbmPartnerTruck ? partnerASNs : filteredPos;
    const packagingPOsOrASNs = isFbmPartnerTruck ? partnerPackagingASNs : packagingPos;
    const formFields = [
        {
            name: 'type',
            label: 'Type',
            inputElement: 'select',
            inputProps: { opts: dropDownOptions },
        },
        {
            name: 'poIDs',
            label: isFbmPartnerTruck ? 'ASNs' : 'POs',
            inputElement: 'autocomplete',
            inputProps: {
                opts: groceryPOsOrASNs
                    .filter((item) => {
                        if (isFbmPartnerTruck) {
                            return true;
                        }

                        // For partial only use server side filter
                        if (poType == 'partial') return true;

                        /**
                         * If the truck is DELIVERED, allow PO's that are delivered and do not have a cross dock
                         * If the truck is PICK UP, allow PO's that are pick up, AND PO's that are delivered with a cross dock
                         */
                        if (deliveryType === 'DELIVERED') {
                            return item.deliveryType === deliveryType && !item.crossDockID;
                        }
                        if (deliveryType === 'PICK UP') {
                            return item.deliveryType === deliveryType || item.crossDockID > 0;
                        }
                        return false;
                    })
                    .map((item) => {
                        if (isFbmPartnerTruck) {
                            return { value: item.id, text: `ASN ${item.id}` };
                        }
                        return { value: item.id, text: `PO ${item.id} - ${item.vendorName}` };
                    }),
                multiple: true,
                onChange: async (e, val, formData, reason, detail) => {
                    if (poType !== 'partial') return;

                    if (reason === 'select-option') {
                        const res = await procurementApi.getPurchaseOrderDetails(detail.option.value);
                        if (!res.data) {
                            setNotification({ level: 'error', text: 'Failed to get PO details' });
                            return;
                        }

                        const poItems = res.data.output.items.map((item) => ({
                            ...item,
                            poID: res.data.output.poID,
                        }));
                        const combinedItems = combineGroupedPartialPoItems(poItems);

                        setPartialPoItems((items) => [...items, ...combinedItems]);
                    } else if (reason === 'remove-option') {
                        const poID = detail.option.value;

                        setSelectedPartialPoSkus((items) => items.filter((item) => item.poID !== poID));
                        setPartialPoItems((items) => items.filter((item) => item.poID !== poID));
                    }
                },
            },
            display: (formData) => formData.type === 'Purchase Order',
            dependencies: ['type'],
        },
        {
            name: 'packagingPoIDs',
            label: 'Packaging POs',
            inputElement: 'autocomplete',
            inputProps: {
                opts: packagingPOsOrASNs.map((item) => ({
                    value: item.id,
                    text: `PO ${item.id} - ${item.vendorName}`,
                })),
                multiple: true,
            },
            display: (formData) => formData.type === 'Packaging Purchase Order',
            dependencies: ['type'],
        },
        {
            name: 'partialPoItems',
            label: 'PO Items',
            display: (formData) => poType === 'partial' && formData.type === 'Purchase Order',
            inputElement: ({ formData, setFormField }) => (
                <Autocomplete
                    multiple
                    options={partialPoItems}
                    value={selectedPartialPoSkus}
                    disableCloseOnSelect
                    getOptionLabel={(option) =>
                        `PO ${option.poID} - ${option.externalSku}: ${option.itemName}; Case Qty: ${option.qty}; Cases/Pallet: ${option.csPallet}`
                    }
                    getOptionSelected={(option, value) => option.poMapID === value.poMapID}
                    renderOption={(option, { selected }) => (
                        <li>
                            <Checkbox checked={selected} style={{ marginRight: 4 }} color="primary" />
                            {`PO ${option.poID} - ${option.externalSku}: ${option.itemName}; Case Qty: ${option.qty}; Cases/Pallet: ${option.csPallet}`}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} placeholder="Select PO Items" />}
                    onChange={(e, val, reason, detail) => {
                        let option;
                        let partialSkuOption;

                        if (detail) {
                            option = detail.option;
                            partialSkuOption = {
                                poID: option.poID,
                                externalSku: option.externalSku,
                                itemName: option.itemName,
                                qty: option.qty,
                                csPallet: option.csPallet,
                                poMapID: option.poMapID,
                                additionalPoMapIds: option.additionalPoMapIds,
                            };
                        }

                        if (reason === 'select-option') {
                            setSelectedPartialPoSkus((items) => [...items, partialSkuOption]);
                        } else if (reason === 'remove-option') {
                            setSelectedPartialPoSkus((items) =>
                                items.filter((item) => item.poMapID !== partialSkuOption.poMapID),
                            );
                        } else if (reason === 'clear') {
                            setSelectedPartialPoSkus([]);
                        }

                        setFormField(selectedPartialPoSkus);
                    }}
                />
            ),
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        if (poType === 'partial') {
                            // make sure each PO has at least one item
                            const numberOfPurchaseOrders = formData.poIDs.length;
                            let counter = 0;

                            if (numberOfPurchaseOrders === 0) {
                                setNotification({ text: 'Must select at least one PO', level: 'error' });
                                return;
                            }

                            formData.poIDs.forEach((po) => {
                                selectedPartialPoSkus.every((item) => {
                                    if (item.poID === po.value) {
                                        counter += 1;
                                        return false;
                                    }
                                    return true;
                                });
                                return true;
                            });

                            if (counter !== numberOfPurchaseOrders) {
                                setNotification({
                                    text: 'Each PO must have at least one item selected',
                                    level: 'error',
                                });
                                return;
                            }
                        }

                        formData.poIDs = formData.poIDs.map((item) => item.value);
                        formData.packagingPoIDs = formData.packagingPoIDs.map((item) => item.value);
                        formData.partialPoItems = expandPartialPoItemMapIds(selectedPartialPoSkus);

                        if (!formData.poIDs.length && !formData.packagingPoIDs.length) {
                            return;
                        }
                        formData.facilityID = user.getFacilityID();

                        const response = await api.addItemsToInboundTruck(truckID, formData);

                        if (response.status === false) {
                            let message = 'Error adding item';
                            if (response.msg) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                            return;
                        }

                        closeModal();
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function EditDateModal({ closeModal, flag, rowData }) {
    const [error, setError] = useState(null);
    const [date, setDate] = useState(null);
    const [time, setTime] = useState(null);
    const [loading, setLoading] = useState(false);

    const formFields = [
        {
            name: 'truckID',
            initialValue: rowData.id,
            inputElement: 'textField',
            label: 'Truck ID',
            gridValue: 4,
            inputProps: { readOnly: true },
        },
        {
            name: 'date',
            initialValue: date,
            inputElement: 'date',
            label: 'Date',
            gridValue: 4,
            inputProps: { required: true },
        },
        {
            name: 'time',
            initialValue: time,
            inputElement: 'time',
            label: 'Time',
            gridValue: 4,
            inputProps: { required: true },
        },
        {
            name: 'bolCompliant',
            inputElement: 'checkbox',
            label: 'BOL Compliant',
            gridValue: 4,
            display: flag === 'checkIn',
        },
        {
            name: 'reasonCode',
            label: 'Reason Code',
            inputElement: 'select',
            inputProps: {
                required: true,
                opts: [
                    { value: 'RESCHEDULED', text: 'Rescheduled' },
                    { value: 'LATE_ARRIVAL', text: 'Late Arrival' },
                    { value: 'NO_SHOW', text: 'No Show' },
                    { value: 'FC_OVERBOOKED', text: 'FC Overbooked' },
                    { value: 'FC_DID_NOT_SCHEDULE', text: 'FC Did Not Schedule' },
                ],
            },
            gridValue: 4,
            display: flag === 'scheduledDelivery',
        },
    ];

    useEffect(() => {
        setDate(dayjs(rowData[flag]).format('YYYY-MM-DD'));
        setTime(dayjs(rowData[flag]).format('HH:mm'));
    }, []);

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <FormComponent
                        formFields={formFields}
                        onSubmit={async (formData) => {
                            setLoading(true);
                            const item = {};

                            item[`${flag}Date`] = formData.date;
                            item[`${flag}Time`] = formData.time;

                            if (flag === 'checkIn') {
                                item.bolCompliant = formData.bolCompliant;
                            }

                            if (flag === 'scheduledDelivery') {
                                item.reasonCode = formData.reasonCode;
                            }

                            const response = await api.editTruckFacilityStopDetails(rowData.facilityStopID, item);
                            if (response.status === false) {
                                let message = 'Error modifying truck';
                                if (response) {
                                    message += `: ${response.msg}`;
                                }
                                setError(`Error updating ${message}`);
                            } else {
                                closeModal();
                            }
                            setLoading(false);
                        }}
                    />
                )}
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

// Modal to upload truck temperatures and summary PDF
function TruckTemperaturesModal({ closeModal, rowData }) {
    const [notification, setNotification] = useState({ level: null, text: null });
    const [truckTemperatures, setTruckTemperatures] = useState([]);
    const formFields = [
        {
            name: 'temperatures',
            inputElement: 'file',
            label: 'Truck Temperatures CSV',
            accept: ['.csv'],
        },
        {
            name: 'summary',
            inputElement: 'file',
            label: 'Summary PDF',
            accept: ['.pdf'],
        },
        {
            name: 'degreesFahrenheit',
            inputElement: 'textField',
            label: 'Temperature Truck Arrived At (F)',
            inputProps: {
                required: false,
                type: 'number',
                step: '0.1',
            },
        },
    ];

    const submitTruckTemperatures = async (formData, resetForm) => {
        try {
            if (!formData.temperatures && !formData.summary && !formData.degreesFahrenheit) {
                setNotification({ text: 'Must provide at least one temperature for this truck', level: 'error' });
                return false;
            }

            if (!formData.temperatures && formData.summary) {
                setNotification({ text: 'Summary PDF was provided but Temperature CSV is missing', level: 'error' });
                return false;
            }

            if (formData.temperatures && !formData.summary) {
                setNotification({ text: 'Temperature CSV was provided but Summary PDF is missing', level: 'error' });
                return false;
            }

            if (formData.temperatures && formData.summary) {
                const uploadResponse = await api.uploadTruckTemperatures(
                    rowData.original.id,
                    rowData.original.facilityStopID,
                    formData,
                );
                if (uploadResponse?.status === false) {
                    let message = 'Error uploading truck temperatures:';
                    if (uploadResponse) {
                        message += ` ${uploadResponse?.msg}.`;
                    }
                    setNotification({ text: message, level: 'error' });
                    return false;
                }
            }

            if (formData.degreesFahrenheit) {
                const body = {
                    degreesFahrenheit: formData.degreesFahrenheit,
                };
                const editResponse = await api.createTruckTemperature(
                    rowData.original.id,
                    rowData.original.facilityStopID,
                    body,
                );
                if (editResponse.status === false) {
                    let message = 'Error creating manual truck temperature:';
                    if (editResponse) {
                        message += ` ${editResponse.msg}.`;
                    }
                    setNotification({ text: message, level: 'error' });
                    return false;
                }
            }

            closeModal();
            setNotification({ text: 'Success', level: 'success' });
            return true;
        } catch (error) {
            setNotification({ text: `Error uploading truck temperatures: ${error.message}`, level: 'error' });
        }
    };

    const getTruckTemperatures = async (truckId, stopId) => {
        const response = await api.getTruckTemperatures(truckId, stopId);
        if (!response.status) {
            setNotification({ level: 'error', text: 'Failed to get truck temperatures' });
            return false;
        }
        setTruckTemperatures(response?.data?.rows);
    };

    useEffect(() => {
        getTruckTemperatures(rowData.original.id, rowData.original.facilityStopID);
    }, []);

    return (
        <Dialog fullWidth maxWidth="md" open onClose={closeModal}>
            <DialogContent>
                <>
                    <Notifications options={notification} />
                    <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>
                        Upload Truck Temperatures - Truck {rowData.original.id}
                        {truckTemperatures.length ? (
                            <span>Last Recorded Temperature - {truckTemperatures[0]?.degreesFahrenheit}°F</span>
                        ) : (
                            ''
                        )}
                    </h2>
                    <FormComponent formFields={formFields} onSubmit={submitTruckTemperatures} />
                </>
            </DialogContent>
        </Dialog>
    );
}

function ViewPartialPoSkus({
    rowData,
    selectedPO,
    setNotification,
    setViewSkusModalOpen,
    skusNotOnTruck = false,
    viewSkusModalOpen,
}) {
    const [tableData, setTableData] = useState([]);
    const removeDisabledMsg = 'Return to main screen to remove PO from this truck';
    const [modalMessage, setModalMessage] = useState('');

    const getPartialPODetails = async () => {
        let res;
        if (skusNotOnTruck) {
            res = await api.getPOItemsAvailableForPartialPOTrucks(rowData.original.id);
            setModalMessage('PO SKUs not on a truck');
        } else {
            res = await api.getPOItemsOnPartialPOTrucks(rowData.original.id, selectedPO, skusNotOnTruck);
            setModalMessage('PO SKUs on truck');
        }
        if (res.status === false) {
            setNotification({ text: `No items found ${res.msg}`, level: 'error' });
            return;
        }

        setTableData(combineGroupedPartialPoItems(res.data.rows));
    };

    useEffect(() => {
        getPartialPODetails();
    }, []);

    const colums = [
        { accessor: 'sku', Header: 'Item Master SKU' },
        { accessor: 'externalSku', Header: 'External SKU' },
        { accessor: 'itemName', Header: 'Name' },
        { accessor: 'qty', Header: 'Case Quantity' },
        { accessor: 'csPallet', Header: 'Cases per Pallet' },
    ];

    if (!skusNotOnTruck) {
        colums.push({
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ row }) => (
                <Tooltip title={tableData.length === 1 ? removeDisabledMsg : ''} placement="top" arrow>
                    <span>
                        <Button
                            id={`Remove_${row.id}`}
                            disabled={tableData.length === 1}
                            onClick={async () => {
                                let cancelled = false;

                                if (tableData.length === 1) {
                                    if (
                                        !window.confirm(
                                            'Removing the last item from a PO will remove the whole PO from this truck. Proceed?',
                                        )
                                    ) {
                                        cancelled = true;
                                    }
                                }

                                if (!cancelled) {
                                    const poMapIdsToRemove = expandPartialPoItemMapIds([row.original]);
                                    const response = await poMapIdsToRemove.reduce(
                                        (promise, poMapID) =>
                                            promise.then(async (prev) => {
                                                if (prev?.status === false) {
                                                    return prev;
                                                }
                                                return api.removeItemFromPartialPOTruck(
                                                    rowData.original.id,
                                                    selectedPO,
                                                    poMapID,
                                                );
                                            }),
                                        Promise.resolve({ status: true }),
                                    );

                                    if (response.status === false) {
                                        setNotification({
                                            text: `Can't remove item ${response.msg}`,
                                            level: 'error',
                                        });
                                        return;
                                    }

                                    getPartialPODetails();
                                }
                            }}
                        >
                            Remove
                        </Button>
                    </span>
                </Tooltip>
            ),
        });
    }

    return (
        <Dialog fullWidth maxWidth="md" open={viewSkusModalOpen} onClose={() => setViewSkusModalOpen(false)}>
            <DialogContent>
                <div>
                    <h2>{modalMessage}</h2>
                </div>
                <div>
                    <DataTable data={tableData} columns={colums} />
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TimeStampColumn({ field, label, onMarkTimeStamp, row, user }) {
    return (
        <div>
            {row.original[field] ? (
                <>{row.original[field]}</>
            ) : (
                <Button
                    sx={{
                        padding: '0px 4px',
                        fontSize: '10px',
                        minWidth: 'auto',
                    }}
                    size="small"
                    variant="contained"
                    onClick={() => onMarkTimeStamp(row.original, field)}
                >
                    {label}
                </Button>
            )}
        </div>
    );
}

function ViewInboundTrucks() {
    const [selectedRow, setSelectedRow] = useState({});
    const [flag, setFlag] = useState({});
    const [editDeliveryModalOpen, setEditDeliveryModalOpen] = useState(false);
    const [truckTempModalOpen, setTruckTempModalOpen] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [carriers, setCarriers] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchObj, setSearchObj] = useState({});
    const [viewSkusModalOpen, setViewSkusModalOpen] = useState(false);
    const [selectedPO, setSelectedPO] = useState();
    const [crossDocks, setCrossDocks] = useState([]);
    const [poLookup, setPoLookup] = useState(null);
    const [facilities, setFacilities] = useState([]);
    const [poNotOnTruck, setPoNotOnTruck] = useState(false);
    const user = useContext(UserContext);

    const showTimeEditModal = (row, field) => {
        setSelectedRow(row);
        setFlag(field);
        setEditDeliveryModalOpen(true);
    };

    const markTruckTimeStamp = async (truck, field) => {
        const now = new Date();

        const currentDate = now.toISOString().split('T')[0];
        const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

        const [time, modifier] = timeString.split(' ');
        let [hours, minutes] = time.split(':').map(Number);

        if (modifier === 'PM' && hours !== 12) {
            hours += 12;
        } else if (modifier === 'AM' && hours === 12) {
            hours = 0;
        }

        const time24hr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

        const item = {
            [`${field}Date`]: currentDate,
            [`${field}Time`]: time24hr,
        };

        const response = await api.editTruckFacilityStopDetails(truck.facilityStopID, item);
        if (response.status === false) {
            setNotification({ text: 'Error marking truck time stamp', level: 'error' });
        } else {
            setNotification({ text: 'Truck time stamp marked', level: 'success' });
            searchInboundTrucks(searchObj);
        }
    };

    const getCrossDocks = async () => {
        const response = await logisticsApi.getInboundCrossDocks(true);
        if (!response.status) {
            setNotification({ text: 'Failed to get cross docks', level: 'error' });
            setCrossDocks([]);
            return;
        }

        const crossDocksAsOpts = response.data.rows.map((item) => ({ text: item.name, value: item.id }));
        crossDocksAsOpts.unshift({ text: '', value: '' });
        setCrossDocks(crossDocksAsOpts);
    };

    const columns = [
        { accessor: 'id', Header: 'Truck ID' },
        {
            Header: 'Ship List',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id={`ShipList_Download_${row.id}`}
                        onClick={async () => {
                            try {
                                await api.downloadTruckShipList(
                                    row.original.id,
                                    user.getFacilityID(),
                                    user.getFacilityName(),
                                );
                            } catch (e) {
                                setNotification({ text: e.toString(), level: 'error' });
                            }
                        }}
                    >
                        Download
                    </Button>
                </div>
            ),
        },
        { accessor: 'multiStopTruck', Header: 'Multi Stop Truck', type: 'checkbox', editable: false },
        { accessor: 'scheduled', Header: 'Scheduled?', type: 'checkbox', editable: false },
        { accessor: 'createdBy', Header: 'Created By' },
        { accessor: 'deliveryType', Header: 'Delivery Type' },
        { accessor: 'referenceNumber', Header: 'Release #', editable: true },
        {
            accessor: 'shipDate',
            Header: 'Ship Date',
            editable: (row) => row.original.deliveryType !== 'DELIVERED',
        },
        { accessor: 'truckCode', Header: 'Truck Code' },
        { accessor: 'shippingClass', Header: 'Shipping Class' },
        { accessor: 'poType', Header: 'PO Type' },
        {
            accessor: 'scheduledDeliveryDate',
            Header: 'Scheduled Delivery Date',
            editable: true,
            customEdit: (row) => showTimeEditModal(row, 'scheduledDelivery'),
        },
        {
            accessor: 'scheduledTime',
            Header: 'Scheduled Delivery Time',
            editable: true,
            customEdit: (row) => showTimeEditModal(row, 'scheduledDelivery'),
        },
        {
            accessor: 'isTruckOnTime',
            Header: 'Truck On-Time for Delivery?',
            cellStyle: (row) => {
                if (row.isTruckOnTime == 'Yes') {
                    return { color: 'green' };
                }
                return { color: 'darkred' };
            },
        },
        {
            accessor: 'inboundCarrierID',
            Header: 'Carrier',
            editable: true,
            editProps: { type: 'select', options: carriers },
        },
        {
            accessor: 'freightCost',
            Header: 'Rate',
            editable: (row) =>
                row.original.deliveryType !== 'DELIVERED' ||
                (row.original.deliveryType == 'DELIVERED' && row.original.freightCost > 0),
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0' },
        },
        {
            accessor: 'additionalShipCost',
            Header: 'Additional Shipping Cost',
            editable: (row) => row.original.deliveryType !== 'DELIVERED',
            editProps: { type: 'input', inputType: 'number', step: '.01', min: '0' },
        },
        {
            accessor: 'minTemperature',
            Header: 'Min Temperature',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'maxTemperature',
            Header: 'Max Temperature',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'dockDoor',
            Header: 'Dock Door',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'numPallets',
            Header: 'Num Pallets',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'totalWeight',
            Header: 'Total Weight',
            editable: true,
            editProps: { type: 'input', inputType: 'number' },
        },
        {
            accessor: 'checkIn',
            Header: 'Check In Time',
            ...(user.user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'checkIn'),
                      editable: true,
                  }
                : {
                      Cell: ({ row }) => (
                          <TimeStampColumn
                              row={row}
                              field="checkIn"
                              label="Check In"
                              onMarkTimeStamp={markTruckTimeStamp}
                              user={user}
                          />
                      ),
                  }),
        },
        {
            accessor: 'checkOut',
            Header: 'Check Out Time',
            ...(user.user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'checkOut'),
                      editable: true,
                  }
                : {
                      Cell: ({ row }) => (
                          <TimeStampColumn
                              row={row}
                              field="checkOut"
                              label="Check Out"
                              onMarkTimeStamp={markTruckTimeStamp}
                              user={user}
                          />
                      ),
                  }),
        },
        {
            accessor: 'dockIn',
            Header: 'Dock In Time',
            ...(user.user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'dockIn'),
                      editable: true,
                  }
                : {
                      Cell: ({ row }) => (
                          <TimeStampColumn
                              row={row}
                              field="dockIn"
                              label="Dock In"
                              onMarkTimeStamp={markTruckTimeStamp}
                              user={user}
                          />
                      ),
                  }),
        },
        {
            accessor: 'unloadStart',
            Header: 'Unload Start Time',
            ...(user.user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'unloadStart'),
                      editable: true,
                  }
                : {
                      Cell: ({ row }) => (
                          <TimeStampColumn
                              row={row}
                              field="unloadStart"
                              label="Unload Start"
                              onMarkTimeStamp={markTruckTimeStamp}
                              user={user}
                          />
                      ),
                  }),
        },
        {
            accessor: 'unloadEnd',
            Header: 'Unload End Time',
            ...(user.user.hasTimestampTrucksRole
                ? {
                      customEdit: (row) => showTimeEditModal(row, 'unloadEnd'),
                      editable: true,
                  }
                : {
                      Cell: ({ row }) => (
                          <TimeStampColumn
                              row={row}
                              field="unloadEnd"
                              label="Unload End"
                              onMarkTimeStamp={markTruckTimeStamp}
                              user={user}
                          />
                      ),
                  }),
        },
        { accessor: 'hoursToUnload', Header: 'Unload Time' },
        {
            accessor: 'bolCompliant',
            Header: 'BOL Compliant',
            type: 'checkbox',
            editable: true,
        },
        { accessor: 'notes', Header: 'Notes', editable: true },
        { accessor: 'status', Header: 'Truck Status' },
        { accessor: 'fbmPartner', Header: 'Partner' },
        {
            accessor: 'crossDockID',
            Header: 'Origin Cross Dock',
            editable: true,
            editProps: { type: 'select', options: crossDocks, required: false },
        },
        {
            Header: 'Cancel',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => {
                if (row.original.status === 'CANCELLED') {
                    return null;
                }
                return (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            id={`Cancel_${row.id}`}
                            onClick={async () => {
                                let msg = 'Cancel truck?';
                                if (row.original.multiStopTruck) {
                                    msg += ` This will cancel all stops for multi stop truck ${row.original.id}`;
                                }
                                if (window.confirm(msg)) {
                                    const response = await api.cancelTruck(row.original.id);
                                    if (response.status === false) {
                                        setNotification({
                                            text: `Failed to cancel truck ${response.msg}`,
                                            level: 'error',
                                        });
                                        return;
                                    }
                                    setNotification({ text: 'Truck cancelled', level: 'success' });
                                    searchInboundTrucks(searchObj);
                                }
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                );
            },
        },
        { accessor: 'createdAt', Header: 'Created At', type: 'date' },
        { accessor: 'destinationCrossDock', Header: 'Destination Cross Dock' },
        {
            Header: 'Upload Temp Summary',
            Cell: ({ row }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id={`TempSummary_Upload_${row.id}`}
                        onClick={() => {
                            setSelectedRow(row);
                            setTruckTempModalOpen(true);
                        }}
                    >
                        Upload
                    </Button>
                </div>
            ),
        },
        {
            Header: 'Download Temp Summary',
            Cell: ({ row }) => {
                if (!row.original.tempSummaryPdfUrl) {
                    return null;
                }
                return (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            id={`TempSummary_Download_${row.id}`}
                            onClick={() => {
                                api.downloadTruckTempSummary(row.original.id, row.original.facilityStopID);
                            }}
                        >
                            Download
                        </Button>
                    </div>
                );
            },
        },
    ];

    const getFacilities = async () => {
        const response = await warehouseApi.getFacilities(true);
        if (response.status === false) {
            return;
        }

        setFacilities(
            response.data.rows.map((row) => {
                row.text = row.name;
                row.value = row.id;
                return row;
            }),
        );
    };

    // Load
    let searchInboundTrucks = async ({ searchBy, searchTerm }) => {
        const search = {};
        setPoNotOnTruck(false);
        const poID = searchTerm;

        if (!!searchBy && !!searchTerm) {
            search[searchBy] = searchTerm;
        }

        setSearchObj(search);
        let response;

        if (poLookup && !!poLookup.facilityID && poLookup.facilityID !== user.getFacilityID()) {
            response = await api.getTrucks(poLookup.facilityID, search);
        } else {
            response = await api.getTrucks(user.getFacilityID(), search);
        }

        if (response.status === false) {
            setNotification({ text: `No trucks found ${response.msg}`, level: 'error' });
            return;
        }

        if (response.data.rows.length === 0) {
            const poRes = await procurementApi.getPurchaseOrderDetails(poID);
            if (!poRes.status) {
                setNotification({ text: `Failed to lookup po: ${poRes.msg}`, level: 'error' });
                return;
            }

            const { facilityID } = poRes.data.output;
            let facilityName = null;
            if (facilityID) {
                facilityName = facilities.find((item) => item.value === facilityID)?.text;
            }

            // check to see if this PO is on a truck in a different FC
            const res = await api.getTrucks(facilityID, search);
            if (res.data.rows.length === 0) {
                setPoNotOnTruck(true);
                return;
            }

            setPoLookup({ poID, facilityID, facilityName });
            if (user.getFacilityClusterIds().includes(poRes.data.facilityID)) {
                setSearchObj({ poID });
            }

            return;
        }

        if (response.data.rows) {
            response.data.rows.map((row) => {
                row.scheduled = !row.unscheduled;
                row.scheduledDeliveryDate = row.scheduledDeliveryTime
                    ? dayjs(row.scheduledDeliveryTime.date).format('YYYY-MM-DD')
                    : null;
                row.scheduledTime = row.scheduledDeliveryTime
                    ? dayjs(row.scheduledDeliveryTime.date).format('HH:mm')
                    : null;
                row.checkIn = row.checkInTime ? dayjs(row.checkInTime.date).format('MM/DD/YYYY HH:mm:ss') : null;
                row.checkOut = row.checkOutTime ? dayjs(row.checkOutTime.date).format('MM/DD/YYYY HH:mm:ss') : null;
                row.dockIn = row.dockInTime ? dayjs(row.dockInTime.date).format('MM/DD/YYYY HH:mm:ss') : null;
                row.unloadStart = row.unloadStartTime
                    ? dayjs(row.unloadStartTime.date).format('MM/DD/YYYY HH:mm:ss')
                    : null;
                row.unloadEnd = row.unloadEndTime ? dayjs(row.unloadEndTime.date).format('MM/DD/YYYY HH:mm:ss') : null;

                const twoHoursInMs = 2 * (60 * 60 * 1000);
                const scheduledDeliveryTimeAsDate =
                    !!row.scheduledDeliveryTime && row.scheduledDeliveryTime.date
                        ? new Date(row.scheduledDeliveryTime.date)
                        : new Date();
                const timeDifference = new Date() - scheduledDeliveryTimeAsDate;
                row.isTruckOnTime =
                    !!row.checkInTime || timeDifference < twoHoursInMs || timeDifference < 0 ? 'Yes' : 'No';

                return row;
            });
        }

        setSearchResults(response.data.rows);
    };

    const getOptionsForInboundTrucks = async () => {
        const res = await api.getInboundOptions();
        if (!res.status) {
            return;
        }
        setCarriers(res.data.carriers.map((item) => ({ text: item.name, value: item.id })));
    };

    useEffect(() => {
        searchInboundTrucks({});
        getOptionsForInboundTrucks();
        getCrossDocks();
        getFacilities();
    }, []);

    const searchByOptions = [
        { value: 'poID', text: 'Purchase Order ID' },
        { value: 'packagingPoID', text: 'Packaging Purchase Order ID' },
    ];

    return (
        <div>
            <h1>Inbound Trucks</h1>
            <Notifications options={notification} />

            <div>
                <div style={{ margin: '0 auto' }}>
                    <Grid container>
                        <Grid item xs={12}>
                            <SearchBar
                                onSubmit={searchInboundTrucks}
                                searchByOptions={searchByOptions}
                                onClearSearch={() => {
                                    setPoLookup({});
                                    setSearchObj({});
                                    setPoNotOnTruck(false);
                                }}
                            />
                        </Grid>
                        {poNotOnTruck && (
                            <Grid item xs={6}>
                                PO is not on a truck
                            </Grid>
                        )}
                        {poLookup && poLookup.poID && poLookup.facilityID && (
                            <>
                                <Grid item xs={6}>
                                    <div>
                                        PO {poLookup.poID} found in facility {poLookup.facilityName}
                                    </div>
                                </Grid>

                                {poNotOnTruck ? (
                                    <Grid item xs={6}>
                                        PO is not on a truck
                                    </Grid>
                                ) : (
                                    <Grid item xs={6}>
                                        {!user.getFacilityClusterIds().includes(poLookup.facilityID) && (
                                            <Button
                                                style={{
                                                    zIndex: 10,
                                                }}
                                                variant="outlined"
                                                onClick={async () => {
                                                    user.setFacilityIDAndName({
                                                        facilityID: poLookup.facilityID,
                                                        facilityName: poLookup.facilityName,
                                                    });
                                                    setSearchObj({
                                                        facilityID: poLookup.facilityID,
                                                        poID: poLookup.poID,
                                                    });

                                                    await searchInboundTrucks({
                                                        searchBy: 'poID',
                                                        searchTerm: poLookup.poID,
                                                    });
                                                    setPoLookup({});
                                                }}
                                            >
                                                Change Facility
                                            </Button>
                                        )}
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>
                </div>
            </div>

            <div>
                {editDeliveryModalOpen && (
                    <EditDateModal
                        rowData={selectedRow}
                        flag={flag}
                        closeModal={() => {
                            setEditDeliveryModalOpen(false);
                            searchInboundTrucks(searchObj);
                        }}
                    />
                )}
                {truckTempModalOpen && (
                    <TruckTemperaturesModal
                        rowData={selectedRow}
                        closeModal={() => {
                            setTruckTempModalOpen(false);
                            setSelectedRow({});
                            searchInboundTrucks(searchObj);
                        }}
                    />
                )}
                {viewSkusModalOpen && (
                    <ViewPartialPoSkus
                        viewSkusModalOpen={viewSkusModalOpen}
                        setViewSkusModalOpen={setViewSkusModalOpen}
                        selectedPO={selectedPO}
                        rowData={selectedRow}
                        setNotification={setNotification}
                    />
                )}
                <DataTable
                    columns={columns}
                    data={searchResults}
                    expandable
                    csvExport
                    ExpansionComponent={
                        <ViewItemsTable
                            setSelectedRow={setSelectedRow}
                            setSelectedPO={setSelectedPO}
                            selectedPO={selectedPO}
                            setViewSkusModalOpen={setViewSkusModalOpen}
                        />
                    }
                    editable
                    rowStyle={(row) => {
                        if (row.status === 'CANCELLED') {
                            return { backgroundColor: 'lightgray' };
                        }
                        return {};
                    }}
                    saveEdit={async ({ field, id, row, value }) => {
                        const item = {};
                        item[field] = value;

                        const facilityStopFields = [
                            'dockDoor',
                            'notes',
                            'totalWeight',
                            'numPallets',
                            'scheduledDeliveryDate',
                            'scheduledTime',
                            'bolCompliant',
                        ];

                        let response;
                        if (facilityStopFields.includes(field)) {
                            if (item.scheduledDeliveryDate) {
                                item.scheduledDeliveryDate = value;
                                item.scheduledDeliveryTime = row.scheduledTime;
                            } else if (item.scheduledTime) {
                                item.scheduledDeliveryDate = row.scheduledDeliveryDate;
                                item.scheduledDeliveryTime = value;
                            }

                            response = await api.editTruckFacilityStopDetails(row.facilityStopID, item);
                        } else {
                            response = await api.editTruckDetails(id, item);
                        }
                        if (response.status === false) {
                            let message = 'Error modifying truck';
                            if (response.msg) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                            return false;
                        }
                        setNotification({ text: 'Truck modified!', level: 'success' });
                        searchInboundTrucks({});
                        return true;
                    }}
                />
            </div>
        </div>
    );
}

function POsNotOnTrucks() {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [viewSkusModalOpen, setViewSkusModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState();
    const user = useContext(UserContext);

    const getPOsNotOnTruck = async () => {
        const response = await api.getPOsNotOnTruck(user.getFacilityID());
        if (response.status === false) {
            setNotification({ text: `No trucks found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }
        setRows(
            response.data.rows.map((row) => {
                row.scheduledDeliveryDate = dayjs(row.scheduledDeliveryDate.date).format('MM/DD/YYYY');
                row.pickupDate = dayjs(row.pickupDate.date).format('MM/DD/YYYY');
                if (row.buyers && Array.isArray(row.buyers)) {
                    row.buyers = row.buyers.map((item) => item.name).join(', ');
                }
                return row;
            }),
        );
    };

    useEffect(() => {
        getPOsNotOnTruck();
    }, []);

    const columns = [
        { Header: 'PO ID', accessor: 'id' },
        {
            Header: 'Info',
            Cell: ({ row }) => {
                if (row.original.unscheduled) {
                    return (
                        <Tooltip
                            title={
                                <div style={{ fontSize: '14px' }}>
                                    This PO is on truck {row.original.fobTruckID}, but has no scheduled delivery time.
                                    {row.original.fobPoType === 'partial' && !row.original.allItemsOnTruck && (
                                        <>
                                            <br />
                                            <br />
                                            This is a partial PO. All items are not on a truck.
                                        </>
                                    )}
                                </div>
                            }
                            placement="top"
                            arrow
                        >
                            <div>
                                <strong>INFO</strong>
                            </div>
                        </Tooltip>
                    );
                }
                return <div>N/A</div>;
            },
        },
        { Header: 'PO Type', accessor: 'poType' },
        { Header: 'Buyers', accessor: 'buyers' },
        { Header: 'Pick Up Date', accessor: 'pickupDate' },
        { Header: 'Scheduled Delivery', accessor: 'scheduledDeliveryDate' },
        { Header: 'Status', accessor: 'status' },
        { Header: 'Delivery Type', accessor: 'deliveryType' },
        { Header: 'Truck Type', accessor: 'fobPoType' },
        { Header: 'Num SKUs', accessor: 'numSkus' },
        { Header: 'Pickup Number', accesor: 'pickupNumber' },
        { Header: 'Shipper', accessor: 'shipperName' },
        { Header: 'Partner', accessor: 'partnerName' },
        {
            Header: 'SKUs Remaining',
            Cell: ({ row }) => {
                if (row.original.fobPoType === 'partial') {
                    return (
                        <div
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                        >
                            <Button
                                id={`View_SKUs_${row.id}`}
                                onClick={() => {
                                    setSelectedRow(row);
                                    setViewSkusModalOpen(true);
                                }}
                            >
                                View SKUs
                            </Button>
                        </div>
                    );
                }
                return <></>;
            },
        },
    ];

    return (
        <div>
            {viewSkusModalOpen && (
                <ViewPartialPoSkus
                    viewSkusModalOpen={viewSkusModalOpen}
                    setViewSkusModalOpen={setViewSkusModalOpen}
                    setNotification={setNotification}
                    rowData={selectedRow}
                    skusNotOnTruck
                />
            )}
            <Notifications options={notification} />
            <DataTable columns={columns} data={rows} />
        </div>
    );
}

export { ViewInboundTrucks, POsNotOnTrucks };
